import { api } from '../../../shared/lib/axios';

/** Férias (change `pessoas-ferias`, US-GP-011A a 014). */

export type PeriodStatus =
  | 'em_aquisicao' | 'disponivel' | 'parcialmente_utilizado' | 'programado' | 'em_gozo' | 'concluido' | 'vencido' | 'perdido' | 'encerrado';
export type Criticality = 'informativo' | 'atencao' | 'alto' | 'critico';

export interface VacationPeriod {
  id: string;
  acquisitionStart: string;
  acquisitionEnd: string;
  concessionStart: string;
  concessionEnd: string;
  absences: number;
  daysEntitled: number;
  taken: number;
  scheduled: number;
  pending: number;
  sold: number;
  available: number;
  unplanned: number;
  status: PeriodStatus;
  criticality: Criticality | null;
  daysToExpire: number | null;
  inconsistent: boolean;
  maxSellDays: number;
}

export interface VacationInstallment {
  id: string;
  seq: number;
  startDate: string;
  endDate: string;
  days: number;
  status: 'pendente' | 'programada' | 'em_gozo' | 'concluida' | 'cancelada';
}

export interface VacationConflict {
  type: 'equipe' | 'feriado' | 'fora_do_concessivo';
  message: string;
}

export interface VacationRequestItem {
  id: string;
  number: number;
  bondId: string;
  periodId: string;
  userId: string;
  personName: string;
  internalRegistration: string;
  status: 'aguardando_aprovacao' | 'devolvida' | 'aprovada' | 'em_gozo' | 'concluida' | 'rejeitada' | 'cancelada' | 'reprogramada';
  currentStep: 'gestor' | 'rh' | null;
  currentResponsible: string | null;
  cycle: number;
  origin: 'colaborador' | 'rh';
  requestedBy: string | null;
  requestedByName: string | null;
  sellDays: number;
  advance13th: boolean;
  notes: string | null;
  replacesRequestId: string | null;
  cancelledReason: string | null;
  createdAt: string;
  period: { acquisitionStart: string; acquisitionEnd: string; concessionEnd: string };
  installments: VacationInstallment[];
  approvals: Array<{ step: string; cycle: number; decision: string; comment: string | null; decidedAt: string; decidedByName: string | null }>;
  conflicts?: VacationConflict[];
}

export interface VacationAlert {
  level: Criticality;
  code: string;
  message: string;
}

export interface VacationEvent {
  id: string;
  type: string;
  label: string;
  origin: 'manual' | 'workflow' | 'automatico';
  notes: string | null;
  createdAt: string;
  requestNumber: number | null;
  actorName: string | null;
}

export interface Vacation360 {
  eligible: boolean;
  identification: {
    userId: string;
    name: string;
    managerName: string | null;
    employmentType: string | null;
    bondId?: string;
    internalRegistration?: string;
    admissionDate?: string;
    bondStatus?: string;
    company?: string | null;
    orgUnit?: string | null;
    jobPosition?: string | null;
    location?: string | null;
  };
  policy: { maxInstallments: number; allowSell: boolean; maxSellDays: number; allow13thAdvance: boolean; minNoticeDays: number } | null;
  summary: {
    available: number;
    scheduled: number;
    taken: number;
    sold: number;
    pending: number;
    expired: number;
    nearExpiry: number;
    pendingRequests: number;
    nextVacation: { startDate: string; endDate: string; days: number } | null;
    onVacation: boolean;
  } | null;
  periods: VacationPeriod[];
  requests: VacationRequestItem[];
  alerts: VacationAlert[];
  events: VacationEvent[];
  lastUpdated: string | null;
}

export interface VacationPolicy {
  id: string;
  companyId: string | null;
  companyName: string | null;
  name: string;
  maxInstallments: number;
  allowSell: boolean;
  maxSellDays: number;
  allow13thAdvance: boolean;
  minNoticeDays: number;
  managerApproval: boolean;
  hrApproval: boolean;
  alertDays: number[];
  active: boolean;
}

export interface SchedulePayload {
  installments: Array<{ startDate: string; endDate: string }>;
  sellDays?: number;
  advance13th?: boolean;
  notes?: string;
}

export interface RequestPayload extends SchedulePayload {
  periodId: string;
  replacesRequestId?: string;
}

export interface PreviewResult {
  errors: string[];
  conflicts: VacationConflict[];
  available: number;
  maxSellDays: number;
}

export interface TeamMemberVacation {
  userId: string;
  name: string;
  onVacation: boolean;
  available: number;
  criticality: Criticality | null;
  nearestConcessionEnd: string | null;
  installments: Array<{ startDate: string; endDate: string; days: number; status: string }>;
}

export interface CalendarResult {
  items: Array<{ id: string; requestId: string; userId: string; name: string; orgUnit: string | null; startDate: string; endDate: string; days: number; status: string }>;
  holidays: Array<{ date: string; name: string }>;
}

/** Quem consulta: RH (por colaborador), o próprio colaborador ou o gestor (liderado). */
export type VacationScope = { kind: 'rh'; userId: string } | { kind: 'me' } | { kind: 'team'; userId: string };

function list<T>(body: unknown): T[] {
  if (Array.isArray(body)) return body as T[];
  const data = (body as { data?: unknown } | null)?.data;
  return Array.isArray(data) ? (data as T[]) : [];
}

function object<T>(body: unknown): T {
  const { version: _v, _links: _l, ...rest } = (body ?? {}) as Record<string, unknown>;
  return rest as T;
}

function base(scope: VacationScope): string {
  if (scope.kind === 'me') return '/me/vacations';
  if (scope.kind === 'team') return `/me/team/members/${scope.userId}/vacations`;
  return `/collaborators/${scope.userId}/vacations`;
}

export async function visaoFerias(scope: VacationScope): Promise<Vacation360> {
  const { data } = await api.get(base(scope));
  return object<Vacation360>(data);
}

export async function eventosFerias(
  scope: VacationScope, filters: { type?: string; origin?: string; from?: string; to?: string },
): Promise<VacationEvent[]> {
  const url = scope.kind === 'rh' ? `/collaborators/${scope.userId}/vacation-events` : '/me/vacation-events';
  const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
  const { data } = await api.get(url, { params });
  return list<VacationEvent>(data);
}

export async function exportarEventosFerias(userId: string, filters: Record<string, string | undefined>): Promise<void> {
  const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
  const response = await api.get(`/collaborators/${userId}/vacation-events/export`, { params, responseType: 'blob' });
  const disposition = String(response.headers['content-disposition'] ?? '');
  const filename = /filename="([^"]+)"/.exec(disposition)?.[1] ?? 'ferias.csv';
  const url = URL.createObjectURL(response.data as Blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

export async function ajustarFaltas(periodId: string, absences: number, justification: string) {
  const { data } = await api.patch(`/vacation-periods/${periodId}/absences`, { absences, justification });
  return object<{ id: string; absences: number; daysEntitled: number }>(data);
}

/** Sem `bondId`, é o autosserviço (`/me`). */
export async function preverFerias(bondId: string | null, payload: RequestPayload): Promise<PreviewResult> {
  const url = bondId ? `/employment-bonds/${bondId}/vacation-requests/preview` : '/me/vacation-requests/preview';
  const { data } = await api.post(url, payload);
  return object<PreviewResult>(data);
}

export async function solicitarFerias(bondId: string | null, payload: RequestPayload): Promise<VacationRequestItem> {
  const url = bondId ? `/employment-bonds/${bondId}/vacation-requests` : '/me/vacation-requests';
  const { data } = await api.post(url, payload);
  return object<VacationRequestItem>(data);
}

export async function reenviarFerias(id: string, payload: SchedulePayload, asHr: boolean): Promise<VacationRequestItem> {
  const { data } = await api.post(asHr ? `/vacation-requests/${id}/resubmit` : `/me/vacation-requests/${id}/resubmit`, payload);
  return object<VacationRequestItem>(data);
}

export async function cancelarFerias(id: string, justification: string | undefined, asHr: boolean): Promise<VacationRequestItem> {
  const { data } = await api.post(asHr ? `/vacation-requests/${id}/cancel` : `/me/vacation-requests/${id}/cancel`, { justification });
  return object<VacationRequestItem>(data);
}

export async function pendentesFerias(as: 'rh' | 'gestor'): Promise<VacationRequestItem[]> {
  const { data } = await api.get(as === 'rh' ? '/vacation-requests/pending' : '/me/team/vacation-requests');
  return list<VacationRequestItem>(data);
}

export async function decidirFerias(as: 'rh' | 'gestor', id: string, decision: string, comment?: string): Promise<VacationRequestItem> {
  const url = as === 'rh' ? `/vacation-requests/${id}/decide` : `/me/team/vacation-requests/${id}/decide`;
  const { data } = await api.post(url, { decision, comment });
  return object<VacationRequestItem>(data);
}

export async function feriasDaEquipe(): Promise<TeamMemberVacation[]> {
  const { data } = await api.get('/me/team/vacations');
  return list<TeamMemberVacation>(data);
}

export async function calendarioFerias(from: string, to: string, orgUnitId?: string): Promise<CalendarResult> {
  const { data } = await api.get('/vacations/calendar', { params: { from, to, ...(orgUnitId ? { orgUnitId } : {}) } });
  return object<CalendarResult>(data);
}

export async function listarPoliticas(): Promise<VacationPolicy[]> {
  const { data } = await api.get('/vacation-policies');
  return list<VacationPolicy>(data);
}

export async function salvarPolitica(id: string | null, payload: Partial<VacationPolicy>): Promise<VacationPolicy> {
  const { data } = id ? await api.patch(`/vacation-policies/${id}`, payload) : await api.post('/vacation-policies', payload);
  return object<VacationPolicy>(data);
}

export interface LocationAddress {
  zipCode: string;
  street: string;
  number: string;
  complement?: string | null;
  district: string;
  cityId: number;
  cityName?: string | null;
}

export interface LocationItem {
  id: string;
  code: string;
  name: string;
  active: boolean;
  address: LocationAddress | null;
}

export async function listarLocalidades(): Promise<LocationItem[]> {
  const { data } = await api.get('/locations', { params: { limit: 500 } });
  return list<LocationItem>(data);
}

export async function definirEnderecoLocalidade(id: string, address: LocationAddress) {
  const { data } = await api.patch(`/locations/${id}/address`, address);
  return object<LocationAddress & { id: string }>(data);
}
